/**
 * Generic DOM element to PNG export utility.
 *
 * FSD: shared/lib — pure utility, no domain logic.
 */

import { toPng } from 'html-to-image';

export interface IExportToPngOptions {
  readonly width: number;
  readonly height: number;
  readonly transform?: string;
  readonly filter?: (node: HTMLElement) => boolean;
  readonly pixelRatio?: number;
  readonly backgroundColor?: string;
}

const DEFAULT_PIXEL_RATIO = 2;
const DEFAULT_BACKGROUND_COLOR = '#ffffff';

/**
 * Captures an HTML element as a PNG data URL.
 */
export async function captureElementToPng(
  element: HTMLElement,
  options: IExportToPngOptions,
): Promise<string> {
  const pixelRatio = options.pixelRatio ?? DEFAULT_PIXEL_RATIO;
  const backgroundColor = options.backgroundColor ?? DEFAULT_BACKGROUND_COLOR;

  const hasTransform = typeof options.transform === 'string' && options.transform.length > 0;

  const dataUrl = await toPng(element, {
    width: options.width,
    height: options.height,
    pixelRatio,
    backgroundColor,
    // Style overrides resize the cloned element. This is ONLY needed when
    // providing a transform via options (the clone must match the target size).
    // When the caller sets the transform directly on the element before capture,
    // style overrides must NOT be applied — otherwise SVG elements with
    // width:100% resize to the export dimensions while their <path> coordinates
    // stay in the original flow space, producing distorted black shapes.
    ...(hasTransform
      ? {
          style: {
            width: `${options.width}px`,
            height: `${options.height}px`,
            transform: options.transform,
          },
        }
      : {}),
    filter: options.filter as ((node: Element) => boolean) | undefined,
  });

  return dataUrl;
}

/**
 * Triggers a browser download for a data URL.
 */
export function downloadDataUrl(dataUrl: string, fileName: string): void {
  const link = document.createElement('a');
  link.download = fileName;
  link.href = dataUrl;
  link.click();
}
